import React, { useEffect, useMemo } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useIntl } from 'react-intl';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import ConfirmDialog from 'components/confirm-dialog';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { ProductCardListSkeleton } from 'components/product/skeleton';
import { toaster } from 'components/toaster/toaster';
import {
  GetUserFavoritesRefsDocument,
  GetUserReorderFavoritesDocument,
  useDeleteFavoriteMutation,
  useGetUserReorderFavoritesQuery,
} from 'generated/graphql-gateway';
import { useNavigation } from 'hooks/navigation/use-navigation';
import withMenuEmptyState from 'pages/menu/components/with-menu-empty-state';
import { useAuthContext } from 'state/auth';
import { useFavoritesContext } from 'state/favorites';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'state/launchdarkly/flags';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { useStoreContext } from 'state/store';
import { isNotOrderable } from 'utils/cart';
import { routes } from 'utils/routing';

import { LoggedOutEmptyState, NoFavoritesEmptyState } from './empty-state';
import { FavoriteCard } from './favorite-card';
import { FavoriteCardData } from './types';

/**
 *
 * FavoritesCardList displays all the favorite orders and allows the guest to reorder, edit, and remove
 *
 */
const InnerFavoritesCardList = () => {
  // Temporary flag to disable reordering of items/combos based on Sanity IDs.
  // Should be removed once One Menu is live.
  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const [removeFavoriteParams, setRemoveFavoriteParams] = React.useState<{
    id: string;
  } | null>(null);
  const { data, loading, refetch } = useGetUserReorderFavoritesQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [deleteFavorite] = useDeleteFavoriteMutation({
    onCompleted: () => toaster.positive(formatMessage({ id: 'removeFavoriteSucess' })),
  });
  const { formatMessage } = useIntl();

  const { navigate, linkTo } = useNavigation();
  const { isAuthenticated } = useAuthContext();
  const { noStoreSelected } = useStoreContext();
  const { setFavorite } = useFavoritesContext();

  const handleEditFavorite = (id: string) => {
    if (noStoreSelected) {
      navigate(routes.storeLocator);
    } else {
      linkTo(`${routes.favorites}/${id}/edit`);
    }
  };

  const handleRemoveFavorite = (id: string) => {
    setRemoveFavoriteParams({ id });
  };

  const favorites = data?.userFavorites?.favorites;

  const { shouldRefetchFavorites, setShouldRefetchFavorites } = useFavoritesContext();
  const shouldRefetch = !favorites || shouldRefetchFavorites;

  useEffect(() => {
    setFavorite(null);
  }, [favorites, setFavorite]);

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetchFavorites(false);
    }
  }, [refetch, setShouldRefetchFavorites, shouldRefetch]);

  const favoriteOrders = useMemo(() => {
    return favorites?.reduce((favsAccum: FavoriteCardData[], favorite) => {
      const entries = (favorite?.entries || ([] as unknown)) as IBackendCartEntries[];
      const isOrderable = !entries.every(entry =>
        isNotOrderable(entry, sanityIdsExcludedFromReordering || [])
      );

      if (entries.length && isOrderable) {
        favsAccum.push({
          id: favorite?._id || '',
          name: favorite?.name || '',
          entries,
        });
      }

      return favsAccum;
    }, []);
  }, [favorites, sanityIdsExcludedFromReordering]);

  const removeFavorite = () => {
    if (removeFavoriteParams?.id) {
      deleteFavorite({
        variables: {
          favoriteId: removeFavoriteParams.id,
        },
        refetchQueries: [
          { query: GetUserReorderFavoritesDocument },
          { query: GetUserFavoritesRefsDocument },
        ],
      });
    }
    setRemoveFavoriteParams(null);
  };

  if (loading) {
    return <ProductCardListSkeleton hasFooter />;
  }

  if (!favoriteOrders?.length) {
    if (!isAuthenticated) {
      return <LoggedOutEmptyState />;
    }
    return <NoFavoritesEmptyState />;
  }

  return (
    <>
      {favoriteOrders?.map((favorite, i) => {
        if (!favorite) {
          return null;
        }

        return (
          <FavoriteCard
            key={i}
            id={favorite.id}
            name={favorite.name}
            entries={favorite.entries}
            onEditFavorite={handleEditFavorite}
            onRemoveFavorite={handleRemoveFavorite}
          />
        );
      })}

      {!!removeFavoriteParams && (
        <ConfirmDialog
          showDialog
          heading={formatMessage({ id: 'removeFavorite' })}
          body={formatMessage({ id: 'removeFavoriteConfirm' })}
          cancelLabel={formatMessage({ id: 'cancel' })}
          confirmLabel={formatMessage({ id: 'remove' })}
          onCancel={() => setRemoveFavoriteParams(null)}
          onConfirm={removeFavorite}
          modalAppearanceEventMessage="Remove Favorite"
        />
      )}
    </>
  );
};

const FavoritesCardList = () => {
  return (
    <ScrollView testID="menu-favorites-page" style={{ marginTop: spacing.$1 }}>
      <CenteredRowContainer>
        <InnerFavoritesCardList />
      </CenteredRowContainer>
    </ScrollView>
  );
};

export default withMenuEmptyState(FavoritesCardList);
